<script lang="ts" setup>
import { computed, ref } from 'vue';
import { DgrDialog, DgrLoading } from '@stockmarkteam/donguri-ui';
import { useStore } from '@/utils/vue';

const store = useStore();

const headerText = computed(() => store.state.confirmationModal.headerText);
const bodyText = computed(() => store.state.confirmationModal.bodyText);
const confirmationType = computed(
  () => store.state.confirmationModal.confirmationType,
);
const btnText = computed(() => store.state.confirmationModal.btnText);
const cancelBtnText = computed(
  () => store.state.confirmationModal.cancelBtnText,
);
const isBtnDisabled = computed(
  () => store.state.confirmationModal.isBtnDisabled,
);
const warningText = computed(() => store.state.confirmationModal.warningText);
const isExecuting = ref(false);
const isLoading = computed(
  () => store.state.confirmationModal.shouldShowLoading && isExecuting.value,
);

const confirm = async () => {
  isExecuting.value = true;
  await store.dispatch('confirmationModal/executeAction');
  isExecuting.value = false;
  hideConfirmationModal();
};

const hideConfirmationModal = () => {
  store.commit('confirmationModal/hideConfirmationModal');
  return true;
};

const bodyTextEmpty = computed(() => bodyText.value.length === 0);
</script>

<template>
  <div>
    <div v-if="isLoading" class="overlay">
      <DgrLoading />
    </div>

    <DgrDialog
      :open="true"
      :title="headerText"
      @cancel="hideConfirmationModal"
      :close-interceptor-handler="hideConfirmationModal"
      :close-on-escape="false"
    >
      <template #description>
        <div class="m-body">
          <pre class="m-body-text c-text-regular--m">{{ bodyText }}</pre>
          <pre
            v-if="warningText"
            class="m-body-text-warning c-text-regular--s"
            >{{ warningText }}</pre
          >
        </div>
      </template>

      <template #footer>
        <div class="m-footer" :class="{ empty: bodyTextEmpty }">
          <div class="m-footer-buttons">
            <button
              class="m-cancel-button c-btn c-btn--auto c-btnOutline"
              @click="hideConfirmationModal"
            >
              {{ cancelBtnText }}
            </button>
            <button
              class="m-create-button c-btn c-btn--auto"
              :class="{
                'c-btn--AnewsPrimary': confirmationType === 'submit',
                'c-btn--negative': confirmationType !== 'submit',
                disabled: isBtnDisabled || isExecuting,
              }"
              @click="isBtnDisabled || isExecuting ? undefined : confirm()"
            >
              {{ btnText }}
            </button>
          </div>
        </div>
      </template>
    </DgrDialog>
  </div>
</template>
<style lang="scss" scoped>
.m-body {
  width: 448px;
  margin-left: -4px;
  margin-bottom: -12px;
  max-height: 360px;
  overflow-y: auto;
  pre.m-body-text {
    white-space: pre-wrap;
  }
  pre.m-body-text-warning {
    white-space: pre-wrap;
    margin-top: 25px;
    color: $color-orange1000;
  }
}
.m-footer {
  &.empty {
    margin-top: -40px;
  }
  .m-footer-buttons {
    display: flex;
    flex-direction: row;
    gap: 24px;
    .m-create-button,
    .m-cancel-button {
      min-width: 120px;
      outline: none;
    }
  }
}
</style>
